import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaClock, FaEye, FaArrowRight, FaSearch } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { safeFetch } from '../utils/apiUtils';
import useSEO, { SITE_URL } from '../utils/useSEO';
import './Blog.css';

const BLOG_SCHEMA = {
    "@context": "https://schema.org",
    "@graph": [
        {
            "@type": "Blog",
            "@id": `${SITE_URL}/blog#blog`,
            "url": `${SITE_URL}/blog`,
            "name": "Nimai Solutions Blog",
            "description": "Insights on web development, mobile apps, UI/UX design, and digital growth for businesses in Vadodara and Gujarat.",
            "publisher": { "@type": "Organization", "@id": `${SITE_URL}/#business`, "name": "Nimai Solutions" }
        },
        {
            "@type": "WebPage",
            "@id": `${SITE_URL}/blog#webpage`,
            "url": `${SITE_URL}/blog`,
            "name": "Blog | Nimai Solutions",
            "isPartOf": { "@id": `${SITE_URL}/#website` },
            "breadcrumb": {
                "@type": "BreadcrumbList",
                "itemListElement": [
                    { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
                    { "@type": "ListItem", "position": 2, "name": "Blog", "item": `${SITE_URL}/blog` }
                ]
            }
        }
    ]
};

const CATEGORIES = ['All', 'Web Development', 'Mobile Development', 'UI/UX Design', 'SEO & Performance', 'Business Growth', 'Case Study'];

const CATEGORY_COLORS = {
    'Web Development':    '#FF9F1C',
    'Mobile Development': '#3b82f6',
    'UI/UX Design':       '#a855f7',
    'SEO & Performance':  '#10b981',
    'Business Growth':    '#ef4444',
    'Case Study':         '#0ea5e9',
    'General':            '#64748b',
};

const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });

const BLOG_API = 'https://aayushrealproperties.in/nimaibackend/public/api/blog-posts';

/* ─── SKELETON ─── */
const SkeletonCard = () => (
    <div className="blog-card skeleton-card">
        <div className="skeleton-img" />
        <div className="blog-card-body">
            <div className="skeleton-line w30" />
            <div className="skeleton-line w100" />
            <div className="skeleton-line w80" />
            <div className="skeleton-line w60" />
        </div>
    </div>
);

/* ─── BLOG CARD ─── */
const BlogCard = ({ post, index }) => {
    const navigate = useNavigate();
    const color = CATEGORY_COLORS[post.category] || CATEGORY_COLORS.General;

    return (
        <motion.article
            className="blog-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: (index % 3) * 0.1 }}
            onClick={() => navigate(`/blog/${post.slug}`)}
            whileHover={{ y: -8 }}
        >
            <div className="blog-card-img-wrap">
                {post.featured_image
                    ? <img src={post.featured_image} alt={post.title} loading="lazy" />
                    : <div className="blog-card-placeholder" style={{ background: `${color}18` }}>
                        <span style={{ fontSize: '3rem' }}>✍</span>
                      </div>
                }
                <span className="blog-cat-tag" style={{ background: color }}>
                    {post.category}
                </span>
            </div>

            <div className="blog-card-body">
                <h2>{post.title}</h2>
                <p className="blog-card-excerpt">{post.excerpt}</p>

                <div className="blog-card-meta">
                    <span><FaClock /> {post.read_time} min read</span>
                    <span><FaEye /> {post.views}</span>
                    <span className="blog-meta-date">{formatDate(post.created_at)}</span>
                </div>

                <span className="blog-read-link">
                    Read Article <FaArrowRight />
                </span>
            </div>
        </motion.article>
    );
};

/* ─── MAIN COMPONENT ─── */
const Blog = () => {
    useSEO({
        title:       "Blog | Web Development, Mobile Apps & Design Insights | Nimai Solutions",
        description: "Expert articles on web development, Flutter app development, UI/UX design, SEO and digital growth for businesses in Vadodara, Gujarat.",
        canonical:   `${SITE_URL}/blog`,
        keywords:    "web development blog Vadodara, Flutter tips India, UI UX design articles Gujarat, digital marketing blog",
        schema:      BLOG_SCHEMA,
    });

    const [posts, setPosts]       = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState('All');
    const [search, setSearch]     = useState('');

    useEffect(() => {
        const fetchPosts = async () => {
            try {
                const { ok, data: result } = await safeFetch(BLOG_API);
                if (ok) {
                    const arr = Array.isArray(result) ? result
                              : Array.isArray(result?.data) ? result.data : [];
                    setPosts(arr);
                }
            } catch (err) {
                console.error('Error fetching blog posts:', err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchPosts();
    }, []);

    const filtered = posts.filter(p => {
        const matchCat  = activeCategory === 'All' || p.category === activeCategory;
        const matchSrch = !search ||
            p.title.toLowerCase().includes(search.toLowerCase()) ||
            (p.excerpt || '').toLowerCase().includes(search.toLowerCase());
        return matchCat && matchSrch;
    });

    return (
        <div className="blog-container">
            <Navbar />

            {/* ── HERO ── */}
            <section className="blog-hero">
                <div className="blog-hero-grid" />
                <motion.div
                    className="blog-hero-content"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <span className="blog-hero-badge">Insights &amp; Resources</span>
                    <h1>Our <span>Blog</span></h1>
                    <p>Expert articles on web development, mobile apps, UI/UX design and digital growth — written by the team at Nimai Solutions.</p>
                </motion.div>
            </section>

            {/* ── SEARCH + FILTERS ── */}
            <div className="blog-controls-wrap">
                <div className="blog-search-bar">
                    <FaSearch className="blog-search-icon" />
                    <input
                        type="text"
                        placeholder="Search articles..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>

                <div className="blog-filters">
                    {CATEGORIES.map(cat => (
                        <button
                            key={cat}
                            className={`blog-filter-btn ${activeCategory === cat ? 'active' : ''}`}
                            onClick={() => setActiveCategory(cat)}
                        >
                            {cat}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── GRID ── */}
            <main className="blog-main">
                {isLoading ? (
                    <div className="blog-grid">
                        {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
                    </div>
                ) : filtered.length === 0 ? (
                    <motion.div className="blog-empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                        <span>📝</span>
                        <h3>No posts found</h3>
                        <p>{search ? `No results for "${search}"` : 'No posts in this category yet. Check back soon!'}</p>
                    </motion.div>
                ) : (
                    <div className="blog-grid">
                        {filtered.map((post, i) => (
                            <BlogCard key={post.id} post={post} index={i} />
                        ))}
                    </div>
                )}
            </main>

            {/* ── CTA ── */}
            <section className="blog-cta-section">
                <div className="blog-cta-card">
                    <div className="cta-blob blob-1" />
                    <div className="cta-blob blob-2" />
                    <div className="blog-cta-inner">
                        <span className="cta-tag">Let's Build Something</span>
                        <h2>Ready to bring your <span>vision</span> to life?</h2>
                        <p>Join forces with our team to create a digital product that stands out and scales.</p>
                        <div className="cta-actions">
                            <button className="cta-primary" onClick={() => window.location.href = '/contact'}>
                                Book a Strategy Call
                            </button>
                            <button className="cta-secondary" onClick={() => window.location.href = '/portfolio'}>
                                View Our Work
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default Blog;
